import {
  Grid,
  FormControl,
  Select,
  MenuItem,
  Switch,
  Icon,
  IconButton,
  InputLabel,
  Autocomplete,
  Stack,
  Tooltip,
} from "@mui/material";

import { Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom"; // Navigation ke liye add kiya
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDInput from "components/MDInput";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CircularIndeterminate from "components/MDLoading";
import { useMaterialUIController } from "context";
import { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updateHelpMessage,
  getPlaces,
  assignSetting,
} from "store/slice/places/placeSlice";
import { updatePlaceSettingsValidationSchema } from "services/validation";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { ArrowBackIos } from "@mui/icons-material";
import { triggerSetupSnackbar } from "store/slice/globalControl/globalControlSlice";

// Extension Limits ka Array defined
const EXTENSION_LIMIT_OPTIONS = [
  ...Array.from({ length: 10 }, (_, i) => ({
    label: `${i + 1}`,
    value: i + 1,
  })),
  { label: "No Limit", value: null },
];

// Reusable Section Card Component (Unchanged)
const SectionCard = ({
  icon,
  title,
  sidenavColor,
  isEditing,
  onEdit,
  onCancel,
  children,
}) => (
  <MDBox
    border="1px solid #e0e0e0"
    borderRadius="xl"
    p={3}
    // mb={3}
    height="100%"
    bgColor="white"
  >
    <MDBox
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mb={2}
    >
      <MDBox display="flex" alignItems="center" gap={1}>
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="2.5rem"
          height="2.5rem"
          borderRadius="md"
          border="1px solid #e0e0e0"
          bgColor={sidenavColor}
          color="#ffffff"
        >
          <Icon fontSize="small">{icon}</Icon>
        </MDBox>
        <MDTypography variant="h6" fontWeight="medium">
          {title}
        </MDTypography>
      </MDBox>

      <MDBox display="flex" gap={1}>
        {isEditing ? (
          <>
            <IconButton
              color="error"
              onClick={onCancel}
              size="small"
              title="Cancel Edit"
            >
              <Icon>close</Icon>
            </IconButton>
            <IconButton
              color="success"
              type="submit"
              size="small"
              title="Save Section"
            >
              <Icon>check</Icon>
            </IconButton>
          </>
        ) : (
          <IconButton
            color="secondary"
            onClick={(e) => {
              e.preventDefault();
              onEdit();
            }}
            size="small"
            title="Edit Section"
          >
            <Icon>edit</Icon>
          </IconButton>
        )}
      </MDBox>
    </MDBox>

    <fieldset
      disabled={!isEditing}
      style={{ border: "none", margin: 0, padding: 0 }}
    >
      <div
        style={{
          opacity: isEditing ? 1 : 0.6,
          transition: "opacity 0.3s ease",
        }}
      >
        {children}
      </div>
    </fieldset>
  </MDBox>
);

const UpdateSettingsPage = () => {
  const formikRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate(); // Navigation hook
  const { id } = useParams();
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const [isLoading, setIsLoading] = useState(false);

  // ✅ Only emit guide events while guide is actively running
  const isGuideActive = useSelector(
    (state) => state.ui?.guide?.active ?? false,
  );
  const [notification, setNotification] = useState({
    color: "",
    icon: "",
    title: "",
    content: "",
    show: false,
    close: () => setNotification({ ...notification, show: false }),
  });
  const [editModes, setEditModes] = useState({
    billing: false,
    experience: false,
    parking: false,
    occupancy: false,
    extension: false,
    chatbot: false,
  });
  const placesList = useSelector((state) => state.places.placesList);

  // ✅ API CALL
  useEffect(() => {
    if (!placesList || placesList.length === 0) {
      dispatch(getPlaces());
    }
  }, [dispatch, placesList]);

  // ✅ CORRECT PLACE
  const data = placesList?.find((item) => item._id === id);

  const handleEdit = (section) => {
    setEditModes((prev) => ({ ...prev, [section]: true }));
  };

  const handleCancel = (section, resetForm) => {
    setEditModes((prev) => ({ ...prev, [section]: false }));
    resetForm();
  };

  const handleSuccess = () => {
    // Fire guide event — GuideOverlay checks transit/active state, so it's safe to always dispatch
    window.dispatchEvent(
      new CustomEvent("GUIDE_STEP_COMPLETE", {
        detail: { step: "PLACE_SETTING" },
      }),
    );
  };

  const handleEditMessage = async (payload, extensionPayload) => {
    try {
      setIsLoading(true);
      const [res, extRes] = await Promise.all([
        dispatch(updateHelpMessage(payload)).unwrap(),
        dispatch(assignSetting(extensionPayload)).unwrap(),
      ]);

      const success = res?.success && extRes?.success;
      setNotification({
        ...notification,
        color: success ? "success" : "error",
        title: success ? "Success" : "Error",
        content: success
          ? "Settings updated successfully."
          : "An unexpected error occurred.",
        icon: success ? "check" : "warning",
        show: true,
      });

      if (success) {
        // ✅ Await so selectedPlace in Redux is synced before snackbar/guide advance
        await dispatch(getPlaces());
        handleSuccess();
        // Use the saved payload's setup (not stale data?.setup) for snackbar evaluation
        dispatch(triggerSetupSnackbar(payload?.setup));
        setEditModes({
          billing: false,
          experience: false,
          parking: false,
          occupancy: false,
          extension: false,
          chatbot: false,
        });
      }
    } catch (err) {
      console.error("Error updating settings:", err);
      setNotification({
        ...notification,
        color: "error",
        title: "Error",
        content: err?.message || "An error occurred while updating settings.",
        icon: "warning",
        show: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Agar data load nahi hua hai to loading state dikhayein
  if (!data) {
    return (
      <CircularIndeterminate
        type="full"
        size={20}
        text="Loading data..."
        open={true}
      />
    );
  }
  return (
    <DashboardLayout>
      <DashboardNavbar />

      <CircularIndeterminate
        type="full"
        size={20}
        text={"Please wait..!"}
        open={isLoading}
      />
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        open={notification.show}
        close={notification.close}
        bgWhite
      />
      <MDBox pt={3} pb={3}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            {/* Page Header */}
            <MDBox
              mb={2}
              mx={1}
              mt={-2}
              py={1}
              px={1}
              variant="gradient"
              bgColor={sidenavColor}
              borderRadius="lg"
              coloredShadow={sidenavColor}
              display="flex"
              alignItems="center"
            >
              <Grid container spacing={2} alignItems="center">
                {/* Left side: Title */}
                <Grid item xs={12} sm="auto">
                  <Stack direction="row" alignItems="center">
                    <IconButton
                      onClick={() => navigate(-1)}
                      sx={{ paddingY: 0 }}
                    >
                      <ArrowBackIos
                        sx={{ color: "#ffffff !important" }}
                        fontSize="small"
                      />
                    </IconButton>
                    <MDTypography variant="h6" color="white">
                      Place Settings ({data?.parkingCode})
                    </MDTypography>
                  </Stack>
                </Grid>

                {/* Right side: Buttons */}
                <Grid
                  item
                  xs={12}
                  sm
                  display="flex"
                  justifyContent={{ xs: "flex-start", sm: "flex-end" }}
                  flexWrap="wrap"
                  gap={1}
                ></Grid>
              </Grid>
            </MDBox>

            {/* Form Content */}
            <Formik
              innerRef={formikRef}
              validationSchema={updatePlaceSettingsValidationSchema}
              enableReinitialize={true}
              initialValues={{
                placeId: data?._id,
                helpText: data?.helpText,
                collectSpaceNumber: data?.collectSpaceNumber || false,
                isVerifyLicensePlate: data?.isVerifyLicensePlate || false,
                isGroupedLocation: data?.isGroupedLocation || false,
                checkOccupancy: data?.checkOccupancy || false,
                isPercentageOccupancyFlow: data?.isPercentageOccupancyFlow || false,
                stopParkingOnOccupancy: data?.stopParkingOnOccupancy || false,
                statementDescriptor: data?.statementDescriptor || "",
                parkingType: data?.isVipParking
                  ? "vip"
                  : data?.isUnitParkingFlow
                    ? "unit"
                    : "normal",
                additionalSpaces: data?.additionalSpaces || 0,
                subscriptionContract: {
                  enabled: data?.subscriptionContract?.enabled || false,
                  document: data?.subscriptionContract?.document || "",
                },
                isExtensionEnable: data?.isExtensionEnable ?? false,
                extensionExpireLimit: data?.extensionExpireLimit ?? "",
                maxExtensionLimit:
                  data?.maxExtensionLimit === undefined
                    ? null
                    : data?.maxExtensionLimit,
                setun: data?.setup,
                allowNewSessionAfterExpiredLink:
                  data?.allowNewSessionAfterExpiredLink ?? false,
                isDockCopyEnable: data?.isDockCopyEnable ?? false,
                dockcopyBotId: data?.dockcopyBotId || "",
              }}
              onSubmit={(value, action) => {
                const currentSetup = data?.setup || {};
                const currentSteps = currentSetup?.steps || {};

                const payload = {
                  placeId: data?._id,
                  helpText: value?.helpText || "",
                  collectSpaceNumber: value?.collectSpaceNumber,
                  additionalSpaces: value?.additionalSpaces,
                  statementDescriptor: value?.statementDescriptor,
                  isVipParking: value.parkingType === "vip",
                  isGroupedLocation: value?.isGroupedLocation,
                  isUnitParkingFlow: value.parkingType === "unit",
                  isVerifyLicensePlate: value?.isVerifyLicensePlate,
                  checkOccupancy: value?.checkOccupancy,
                  isPercentageOccupancyFlow: value?.isPercentageOccupancyFlow,
                  stopParkingOnOccupancy: value?.stopParkingOnOccupancy,
                  isDockCopyEnable: value?.isDockCopyEnable ?? false,
                  dockcopyBotId: value?.dockcopyBotId || "",
                  subscriptionContract: {
                    enabled: value.subscriptionContract.enabled,
                    document: value.subscriptionContract.document,
                  },
                  setup: {
                    ...currentSetup,
                    steps: {
                      ...currentSteps,
                      placeSettings: true,
                    },
                  },
                };

                const extensionPayload = {
                  placeId: data?._id,
                  isExtensionEnable: value?.isExtensionEnable,
                  extensionExpireLimit: value?.extensionExpireLimit,
                  maxExtensionLimit: value?.maxExtensionLimit,
                  allowNewSessionAfterExpiredLink:
                    value?.allowNewSessionAfterExpiredLink,
                };

                handleEditMessage(payload, extensionPayload);
              }}
            >
              {(props) => (
                <form onSubmit={props.handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      {/* SECTION 1: Billing & Identity */}
                      <SectionCard
                        icon="credit_card"
                        title="Billing & Identity"
                        sidenavColor={sidenavColor}
                        isEditing={editModes.billing}
                        onEdit={() => handleEdit("billing")}
                        onCancel={() =>
                          handleCancel("billing", props.resetForm)
                        }
                      >
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <MDTypography
                              variant="caption"
                              fontWeight="bold"
                              color="text"
                              textTransform="uppercase"
                            >
                              Help Text For Receipt
                            </MDTypography>
                            <MDInput
                              fullWidth
                              name="helpText"
                              placeholder="Enter receipt help text..."
                              value={props.values.helpText}
                              onChange={props.handleChange}
                              onBlur={props.handleBlur}
                              error={
                                props.errors.helpText && props.touched.helpText
                                  ? true
                                  : false
                              }
                              success={
                                props.errors.helpText && props.touched.helpText
                                  ? false
                                  : true
                              }
                              helperText={
                                props.errors.helpText && props.touched.helpText
                                  ? props.errors.helpText
                                  : null
                              }
                              sx={{ mt: 1 }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <MDTypography
                              variant="caption"
                              fontWeight="bold"
                              color="text"
                              textTransform="uppercase"
                            >
                              Statement Descriptor
                            </MDTypography>
                            <MDInput
                              fullWidth
                              name="statementDescriptor"
                              placeholder="parking place"
                              value={props.values.statementDescriptor}
                              onChange={props.handleChange}
                              onBlur={props.handleBlur}
                              error={
                                props.errors.statementDescriptor &&
                                props.touched.statementDescriptor
                                  ? true
                                  : false
                              }
                              success={
                                props.errors.statementDescriptor &&
                                props.touched.statementDescriptor
                                  ? false
                                  : true
                              }
                              helperText={
                                props.errors.statementDescriptor &&
                                props.touched.statementDescriptor
                                  ? props.errors.statementDescriptor
                                  : null
                              }
                              sx={{ mt: 1 }}
                            />
                          </Grid>
                        </Grid>
                      </SectionCard>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {/* SECTION 2: User Experience */}
                      <SectionCard
                        icon="people"
                        title="User Experience"
                        sidenavColor={sidenavColor}
                        isEditing={editModes.experience}
                        onEdit={() => handleEdit("experience")}
                        onCancel={() =>
                          handleCancel("experience", props.resetForm)
                        }
                      >
                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <MDBox
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <MDBox pr={2}>
                                <MDTypography
                                  variant="button"
                                  fontWeight="medium"
                                  display="block"
                                >
                                  Enable space input in license plate form?
                                </MDTypography>
                                <MDTypography variant="caption" color="text">
                                  Allows users to type spaces in the plate field
                                </MDTypography>
                              </MDBox>
                              <Switch
                                checked={props.values.collectSpaceNumber}
                                onChange={(e) =>
                                  props.setFieldValue(
                                    "collectSpaceNumber",
                                    e.target.checked,
                                  )
                                }
                                color="success"
                              />
                            </MDBox>
                          </Grid>
                          <Grid item xs={12}>
                            <MDBox
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <MDBox pr={2}>
                                <MDTypography
                                  variant="button"
                                  fontWeight="medium"
                                  display="block"
                                >
                                  Enable contract signing for subscriptions?
                                </MDTypography>
                                <MDTypography variant="caption" color="text">
                                  Requires digital signature for long-term
                                  parking
                                </MDTypography>
                              </MDBox>
                              <Switch
                                checked={
                                  props.values?.subscriptionContract?.enabled
                                }
                                onChange={(e) =>
                                  props.setFieldValue(
                                    "subscriptionContract.enabled",
                                    e.target.checked,
                                  )
                                }
                                color="success"
                              />
                            </MDBox>
                          </Grid>
                          {props.values.subscriptionContract.enabled && (
                            <Grid item xs={12}>
                              <MDInput
                                fullWidth
                                name="subscriptionContract.document"
                                label="Contract Document URL"
                                value={
                                  props.values.subscriptionContract.document
                                }
                                onChange={props.handleChange}
                                onBlur={props.handleBlur}
                                error={
                                  !!props.errors.subscriptionContract
                                    ?.document &&
                                  props.touched.subscriptionContract?.document
                                }
                                success={
                                  !props.errors.subscriptionContract
                                    ?.document &&
                                  props.touched.subscriptionContract?.document
                                }
                                helperText={
                                  props.touched.subscriptionContract?.document
                                    ? props.errors.subscriptionContract
                                        ?.document
                                    : null
                                }
                              />
                            </Grid>
                          )}
                        </Grid>
                      </SectionCard>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {/* SECTION 3: Parking Configuration */}
                      <SectionCard
                        icon="directions_car"
                        title="Parking Configuration"
                        sidenavColor={sidenavColor}
                        isEditing={editModes.parking}
                        onEdit={() => handleEdit("parking")}
                        onCancel={() =>
                          handleCancel("parking", props.resetForm)
                        }
                      >
                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <MDTypography
                              variant="caption"
                              fontWeight="bold"
                              color="text"
                              textTransform="uppercase"
                              display="block"
                              mb={1}
                            >
                              Choose Parking Flow
                            </MDTypography>
                            <MDBox display="flex" gap={2}>
                              {[
                                { value: "normal", label: "Normal Parking" },
                                { value: "vip", label: "Vip Parking" },
                                { value: "unit", label: "Unit Parking" },
                              ].map((type) => {
                                const isSelected =
                                  props.values.parkingType === type.value;

                                return (
                                  <MDButton
                                    key={type.value}
                                    variant={
                                      isSelected ? "contained" : "outlined"
                                    }
                                    color={
                                      isSelected ? sidenavColor : "secondary"
                                    }
                                    onClick={() =>
                                      props.setFieldValue(
                                        "parkingType",
                                        type.value,
                                      )
                                    }
                                    disabled={!editModes.parking}
                                    sx={{ px: 3, textTransform: "capitalize" }}
                                  >
                                    {isSelected && (
                                      <Icon sx={{ mr: 1 }}>check</Icon>
                                    )}
                                    {type.label}
                                  </MDButton>
                                );
                              })}
                            </MDBox>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                              <InputLabel id="select-spaces-label">
                                Additional Spaces
                              </InputLabel>
                              <Select
                                disabled={!editModes.parking}
                                labelId="select-spaces-label"
                                label="Additional Spaces"
                                value={props.values?.additionalSpaces}
                                onChange={(e) => {
                                  const count = Number(e.target.value);
                                  props.setFieldValue(
                                    "additionalSpaces",
                                    count,
                                  );
                                }}
                              >
                                {Array.from({ length: 100 }, (_, i) => i).map(
                                  (num) => (
                                    <MenuItem key={num} value={num}>
                                      {num}
                                    </MenuItem>
                                  ),
                                )}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            md={6}
                            display="flex"
                            alignItems="flex-end"
                            pb={1}
                          >
                            <MDBox display="flex" alignItems="center" gap={1}>
                              <MDTypography
                                variant="button"
                                fontWeight="regular"
                              >
                                Is that Group Location?
                              </MDTypography>
                              <Switch
                                checked={props.values.isGroupedLocation}
                                onChange={(e) =>
                                  props.setFieldValue(
                                    "isGroupedLocation",
                                    e.target.checked,
                                  )
                                }
                                color="success"
                              />
                            </MDBox>
                          </Grid>
                        </Grid>
                      </SectionCard>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {/* SECTION 4: Occupancy & Validation */}
                      <SectionCard
                        icon="info_outline"
                        title="Occupancy & Validation"
                        sidenavColor={sidenavColor}
                        isEditing={editModes.occupancy}
                        onEdit={() => handleEdit("occupancy")}
                        onCancel={() =>
                          handleCancel("occupancy", props.resetForm)
                        }
                      >
                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            <MDBox
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <MDBox>
                                <MDTypography
                                  variant="button"
                                  fontWeight="medium"
                                  display="block"
                                >
                                  Verify License plate
                                </MDTypography>
                                <MDTypography variant="caption" color="text">
                                  Validate license plate format before session
                                  starts
                                </MDTypography>
                              </MDBox>
                              <Switch
                                checked={props.values.isVerifyLicensePlate}
                                onChange={(e) =>
                                  props.setFieldValue(
                                    "isVerifyLicensePlate",
                                    e.target.checked,
                                  )
                                }
                                color="success"
                              />
                            </MDBox>
                          </Grid>

                          <Grid item xs={12}>
                            <MDBox display="flex" alignItems="center" gap={1}>
                              <MDTypography
                                variant="button"
                                fontWeight="medium"
                              >
                                Check Occupancy
                              </MDTypography>
                              <Switch
                                checked={props.values.checkOccupancy}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  props.setFieldValue(
                                    "checkOccupancy",
                                    checked,
                                  );
                                  if (!checked) {
                                    props.setFieldValue(
                                      "stopParkingOnOccupancy",
                                      false,
                                    );
                                    props.setFieldValue(
                                      "isPercentageOccupancyFlow",
                                      false,
                                    );
                                  }
                                }}
                                color="success"
                              />
                            </MDBox>
                          </Grid>
                          <Grid item xs={12}>
                            {props.values.checkOccupancy && (
                              <MDBox display="flex" alignItems="center" gap={1}>
                                <MDTypography
                                  variant="button"
                                  fontWeight="medium"
                                >
                                  Percentage Occupancy Flow
                                </MDTypography>
                                <Switch
                                  checked={props.values.isPercentageOccupancyFlow}
                                  onChange={(e) =>
                                    props.setFieldValue(
                                      "isPercentageOccupancyFlow",
                                      e.target.checked,
                                    )
                                  }
                                  color="success"
                                />
                              </MDBox>
                            )}
                          </Grid>
                          <Grid item xs={12}>
                            {props.values.checkOccupancy && (
                              <MDBox display="flex" alignItems="center" gap={1}>
                                <MDTypography
                                  variant="button"
                                  fontWeight="medium"
                                >
                                  Stop Parking on 100% Occupancy
                                </MDTypography>
                                <Switch
                                  checked={props.values.stopParkingOnOccupancy}
                                  onChange={(e) =>
                                    props.setFieldValue(
                                      "stopParkingOnOccupancy",
                                      e.target.checked,
                                    )
                                  }
                                  color="success"
                                />
                              </MDBox>
                            )}
                          </Grid>
                        </Grid>
                      </SectionCard>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {/* SECTION 5: Extension Settings */}
                      <SectionCard
                        icon="more_time"
                        title="Extension Settings"
                        sidenavColor={sidenavColor}
                        isEditing={editModes.extension}
                        onEdit={() => handleEdit("extension")}
                        onCancel={() =>
                          handleCancel("extension", props.resetForm)
                        }
                      >
                        <Grid container spacing={3}>
                          {/* Enable Extension */}
                          <Grid item xs={12}>
                            <MDBox
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <MDBox pr={2}>
                                <MDTypography
                                  variant="button"
                                  fontWeight="medium"
                                  display="block"
                                >
                                  Enable Reservation Extension
                                </MDTypography>
                                <MDTypography variant="caption" color="text">
                                  Allows active reservations to be extended by
                                  the user
                                </MDTypography>
                              </MDBox>
                              <Switch
                                checked={props.values.isExtensionEnable}
                                onChange={(e) =>
                                  props.setFieldValue(
                                    "isExtensionEnable",
                                    e.target.checked,
                                  )
                                }
                                color="success"
                              />
                            </MDBox>
                          </Grid>
                          {/* Allow New Session After Expired Link */}
                          <Grid item xs={12}>
                            <MDBox
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <MDBox pr={2}>
                                <MDBox
                                  display="flex"
                                  alignItems="center"
                                  gap={0.5}
                                >
                                  <MDTypography
                                    variant="button"
                                    fontWeight="medium"
                                  >
                                    Allow New Session After Expired Link
                                  </MDTypography>

                                  <Tooltip
                                    title="When enabled, users can create a new parking session even if the extension link has expired. If disabled, users will see the link expired message."
                                    arrow
                                    placement="top"
                                  >
                                    <Icon
                                      fontSize="small"
                                      sx={{
                                        cursor: "pointer",
                                        color: "text.secondary",
                                      }}
                                    >
                                      info_outline
                                    </Icon>
                                  </Tooltip>
                                </MDBox>
                              </MDBox>

                              <Switch
                                checked={
                                  props.values.allowNewSessionAfterExpiredLink
                                }
                                onChange={(e) =>
                                  props.setFieldValue(
                                    "allowNewSessionAfterExpiredLink",
                                    e.target.checked,
                                  )
                                }
                                color="success"
                              />
                            </MDBox>
                          </Grid>

                          {/* Expire Limit */}
                          <Grid item xs={12} md={6}>
                            <Autocomplete
                              options={[15, 20, 25, 30, 35, 40, 45, 50, 55, 60]}
                              value={props.values.extensionExpireLimit || null}
                              onChange={(_, value) =>
                                props.setFieldValue(
                                  "extensionExpireLimit",
                                  value,
                                )
                              }
                              disabled={!props.values.isExtensionEnable}
                              disableClearable
                              renderInput={(params) => (
                                <MDInput
                                  {...params}
                                  label="Extension Expire Limit (Minutes)"
                                  fullWidth
                                />
                              )}
                            />
                          </Grid>

                          {/* Max Extension Limit */}
                          <Grid item xs={12} md={6}>
                            <Autocomplete
                              options={EXTENSION_LIMIT_OPTIONS}
                              getOptionLabel={(o) => o.label}
                              value={
                                EXTENSION_LIMIT_OPTIONS.find(
                                  (o) =>
                                    o.value === props.values.maxExtensionLimit,
                                ) || null
                              }
                              onChange={(_, option) =>
                                props.setFieldValue(
                                  "maxExtensionLimit",
                                  option?.value ?? null,
                                )
                              }
                              disabled={!props.values.isExtensionEnable}
                              renderInput={(params) => (
                                <MDInput
                                  {...params}
                                  label="Max Extension Limit"
                                  fullWidth
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      </SectionCard>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {/* SECTION 6: Chatbot UI Settings */}
                      <SectionCard
                        icon="smart_toy"
                        title="Chatbot UI Settings"
                        sidenavColor={sidenavColor}
                        isEditing={editModes.chatbot}
                        onEdit={() => handleEdit("chatbot")}
                        onCancel={() =>
                          handleCancel("chatbot", props.resetForm)
                        }
                      >
                        <Grid container spacing={2}>
                          {/* Choose Chatbot Interface */}
                          <Grid item xs={12}>
                            <MDTypography
                              variant="caption"
                              fontWeight="bold"
                              color="text"
                              textTransform="uppercase"
                              display="block"
                              mb={1}
                            >
                              Choose Chatbot Interface
                            </MDTypography>
                            <MDBox display="flex" gap={2} flexWrap="wrap">
                              {[
                                {
                                  isDockCopy: false,
                                  label: "Botcopy",
                                },
                                {
                                  isDockCopy: true,
                                  label: "DC (Dock Copy)",
                                },
                              ].map((option) => {
                                const isSelected =
                                  Boolean(props.values.isDockCopyEnable) ===
                                  option.isDockCopy;

                                return (
                                  <MDButton
                                    key={option.label}
                                    variant={
                                      isSelected ? "contained" : "outlined"
                                    }
                                    color={
                                      isSelected ? sidenavColor : "secondary"
                                    }
                                    onClick={() =>
                                      props.setFieldValue(
                                        "isDockCopyEnable",
                                        option.isDockCopy,
                                      )
                                    }
                                    disabled={!editModes.chatbot}
                                    sx={{
                                      px: 3,
                                      textTransform: "none",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {isSelected && (
                                      <Icon sx={{ mr: 1 }}>check</Icon>
                                    )}
                                    {option.label}
                                  </MDButton>
                                );
                              })}
                            </MDBox>
                          </Grid>

                          {/* Quick Toggle Switch */}
                          <Grid item xs={12}>
                            <MDBox
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              pt={1}
                            >
                              <MDBox pr={2}>
                                <MDTypography
                                  variant="button"
                                  fontWeight="medium"
                                  display="block"
                                >
                                  Enable Dock Copy (DC)
                                </MDTypography>
                                <MDTypography variant="caption" color="text">
                                  Toggle to use Dock Copy (DC) instead of
                                  Botcopy
                                </MDTypography>
                              </MDBox>
                              <Switch
                                checked={Boolean(props.values.isDockCopyEnable)}
                                onChange={(e) =>
                                  props.setFieldValue(
                                    "isDockCopyEnable",
                                    e.target.checked,
                                  )
                                }
                                disabled={!editModes.chatbot}
                                color="success"
                              />
                            </MDBox>
                          </Grid>

                          {/* Dock Copy Bot ID Input */}
                          {props.values.isDockCopyEnable && (
                            <Grid item xs={12}>
                              <MDInput
                                fullWidth
                                name="dockcopyBotId"
                                label="Dock Copy Bot ID"
                                placeholder="Enter Dock Copy Bot ID..."
                                value={props.values.dockcopyBotId || ""}
                                onChange={props.handleChange}
                                onBlur={props.handleBlur}
                                disabled={!editModes.chatbot}
                                error={
                                  props.errors.dockcopyBotId &&
                                  props.touched.dockcopyBotId
                                    ? true
                                    : false
                                }
                                success={
                                  !props.errors.dockcopyBotId &&
                                  Boolean(props.values.dockcopyBotId)
                                }
                                helperText={
                                  props.errors.dockcopyBotId &&
                                  props.touched.dockcopyBotId
                                    ? props.errors.dockcopyBotId
                                    : "Unique Bot ID for Dock Copy chatbot interface"
                                }
                              />
                            </Grid>
                          )}

                          {/* Informational Status Card */}
                          <Grid item xs={12}>
                            <MDBox
                              p={2}
                              borderRadius="md"
                              bgColor={
                                props.values.isDockCopyEnable
                                  ? sidenavColor
                                  : "grey-100"
                              }
                              variant={
                                props.values.isDockCopyEnable
                                  ? "gradient"
                                  : "contained"
                              }
                              coloredShadow={
                                props.values.isDockCopyEnable
                                  ? sidenavColor
                                  : undefined
                              }
                              color={
                                props.values.isDockCopyEnable ? "white" : "text"
                              }
                              display="flex"
                              alignItems="center"
                              gap={1.5}
                            >
                              <Icon
                                fontSize="medium"
                                sx={{
                                  color: props.values.isDockCopyEnable
                                    ? "#ffffff !important"
                                    : "inherit",
                                }}
                              >
                                {props.values.isDockCopyEnable
                                  ? "bolt"
                                  : "smart_toy"}
                              </Icon>
                              <MDBox>
                                <MDTypography
                                  variant="button"
                                  fontWeight="bold"
                                  color={
                                    props.values.isDockCopyEnable
                                      ? "white"
                                      : "dark"
                                  }
                                  display="block"
                                >
                                  Active:{" "}
                                  {props.values.isDockCopyEnable
                                    ? `DC (Dock Copy)${
                                        props.values.dockcopyBotId
                                          ? ` — Bot ID: ${props.values.dockcopyBotId}`
                                          : ""
                                      }`
                                    : "Botcopy"}
                                </MDTypography>
                                <MDTypography
                                  variant="caption"
                                  color={
                                    props.values.isDockCopyEnable
                                      ? "white"
                                      : "text"
                                  }
                                >
                                  {props.values.isDockCopyEnable
                                    ? "Customers at this location will interact with the Dock Copy (DC) chatbot interface."
                                    : "Customers at this location will interact with the Botcopy chatbot interface."}
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                          </Grid>
                        </Grid>
                      </SectionCard>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
};

export default UpdateSettingsPage;
